/* eslint-disable max-lines */ // Todo JT-122757 split code and remove this line
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useGlobalSearchParams,
  useRouter,
} from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import {
  SourceName,
  useApplicationTrackErrorEvents,
} from 'features/application/hooks/useApplicationTrackErrorEvents';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import VaroTurnstile from 'features/auth/components/VaroTurnstile/VaroTurnstile';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import useRedirect from 'features/otp/hooks/useRedirection/useRedirection';
import SignupHeader from 'features/signup/components/SignupHeader/SignupHeader';
import SignupHeaderVariant from 'features/signup/components/SignupHeaderVariant/SignupHeaderVariant';
import {
  KEYBOARD_HEIGHT_FOR_PHONE_KEYPAD,
  KEYBOARD_HEIGHT_FOR_TEXT_KEYPAD,
  KEYBOARD_SCROLL_OFFSET_EMAIL,
  KEYBOARD_SCROLL_OFFSET_PHONE_NUMBER,
  SIGNUP_HEADER_VARIANT,
} from 'features/signup/constants';
import { createAccountVerificationSchema } from 'features/signup/CreateAccount/CreateAccount.schema';
import { useCampaignSessionTracking } from 'features/signup/hooks/useCampaignSessionTracking';
import useCreateAccount, {
  CreateAccountResponse,
} from 'features/signup/hooks/useCreateAccount';
import useReferrals from 'features/signup/hooks/useReferrals';
import useSignupHeader from 'features/signup/hooks/useSignupHeader';
import useSubmitAttributions, {
  getCampaignObject,
  getCampaignObjectAnalytics,
} from 'features/signup/hooks/useSubmitAttributions';
import {
  getAndroidKeyboardHeightOffset,
  getParam,
} from 'features/signup/utils/signupUtils';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  Linking,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getAttributionData } from 'services/analytics/analyticsManager';
import { clearPendingDeeplink } from 'services/deeplinks/deeplinkManager';
import { syncMinimalUserIdentity } from 'services/userInfo/userInfoManager';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import {
  getPasswordValidationStatus,
  passwordValidationMessages,
} from 'shared/components/vxl/input/schemas/password.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import { isAndroid, isIOS, isWeb } from 'shared/constants/platform';
import {
  campaign,
  EMAIL,
  PHONENUMBER,
  REFERRAL_QUERY_PARAM,
  REFERRAL_QUERY_PARAM_1,
  REFERRAL_TERMS,
} from 'shared/constants/urlParams';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import {
  ActionType,
  useAnalyticsScreenKey,
} from 'shared/hooks/analytics/useAnalyticsScreenKey';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  DynamicConfigurationKey,
  FeatureFlagKey,
} from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import {
  deleteRefreshTokenForReactNativeUserKey,
  saveBiometricToggleSetting,
  saveUserIDForReactNative,
} from 'shared/utils/biometricAuth/biometricAuth';
import { extractCookies } from 'shared/utils/extractCookies/extractCookies.web';
import hasCaptchaToken from 'shared/utils/hasCaptchaToken';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';
import {
  addTag,
  logError,
  logErrorWithContext,
} from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
  SentryTags,
} from 'shared/utils/sentry/sentry.constants';
import { getEmailDomainSuggestion } from 'shared/utils/string/email';
import {
  clearPhoneNumberFormatting,
  formatDefaultPhoneNumber,
  formatPhoneInputText,
  PHONE_MAX_LENGTH,
  PHONE_PREFIX,
} from 'shared/utils/string/phone';

const SNACKBAR_DURATION = 2750;
const ACQUISITION_CAPTURED_ANALYTICS_ID = 'acquisitionCaptured';

export enum AppsFlyerInstallStatus {
  Organic = 'Organic',
  NonOrganic = 'Non-organic',
}

export default function CreateAccount() {
  const router = useRouter();
  // Clear any pending authenticated deeplink when user navigates to signup
  // This prevents unwanted redirects after signup completion
  useEffect(() => {
    void clearPendingDeeplink();
  }, []);

  const { segmentClient, analyticsEvents } = useAnalyticsConfiguration();
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'createAccount',
  });
  const [xVaroCToken, setXVaroCToken] = useState<string>('');
  const { getDynamicConfiguration, isFeatureEnabled } = useStatsig();
  const config = getDynamicConfiguration(
    DynamicConfigurationKey.AGREEMENT_ON_SIGNUP_SCREEN,
  );
  const agreementUrl: Href = config?.agreement_url as Href;
  const { bottom = 0 } = useSafeAreaInsets();
  const { logErrorEvent } = useApplicationTrackErrorEvents();

  useStepStartedFocusAnalytics({
    analyticsId: 'signupStepStarted',
    customAttributes: {
      step_name: 'signup_identity',
    },
  });

  const handlePasswordMaskAnalytics = usePressableAnalytics({
    analyticsId: 'passwordMaskClicked',
  });

  const handleAgreementClickedAnalytics = usePressableAnalytics({
    analyticsId: 'agreementClicked',
    customAttributes: {
      url: agreementUrl as string,
    },
  });

  const handleSubmitClickedAnalytics = usePressableAnalytics({
    analyticsId: 'createAccountCompleted',
    customAttributes: {
      step_name: 'signup_identity',
    },
  });

  const applicationFlowErrorEncounteredAnalytics = usePressableAnalytics({
    analyticsId: 'applicationFlowErrorEncountered',
  });
  const screenKey = useAnalyticsScreenKey(ActionType.PRESSABLE);
  const [passwordValidations, setPasswordValidations] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSignUpLoading, setIsSignUpLoading] = useState(false);
  const {
    getVaroSessionId,
    storeIdToken,
    isLoggedIn,
    idToken,
    fetchIovationToken,
    resetAuthState,
  } = useContext<AuthContextProps>(AuthContext);
  const { postCreateAccount } = useCreateAccount();
  const urlSearchParams = useGlobalSearchParams();
  const { postSubmitAttributions } = useSubmitAttributions();
  const [snackbar, setSnackbar] = useState<{
    message: string;
  }>({
    message: '',
  });

  const referralCode = getParam(urlSearchParams, REFERRAL_QUERY_PARAM);
  const referralCode1 = getParam(urlSearchParams, REFERRAL_QUERY_PARAM_1);
  const effectiveReferralCode = referralCode || referralCode1;
  const campaignCode = getParam(urlSearchParams, campaign.campaign);

  useCampaignSessionTracking(urlSearchParams);

  const defaultPhoneNumber = getParam(urlSearchParams, PHONENUMBER);

  const phoneNumber = formatDefaultPhoneNumber(defaultPhoneNumber || '');

  const onPressSeeFullTerms = async () => {
    await Linking.openURL(
      campaignCode ? `${REFERRAL_TERMS}${campaignCode}` : REFERRAL_TERMS,
    );
  };

  const paramEmail = getParam(urlSearchParams, EMAIL);
  const defaultEmail = paramEmail
    ? decodeURIComponent(paramEmail.replace(/\s/g, '+'))
    : '';

  const {
    isFetching,
    hasInvalidReferralCode,
    userName,
    isValidReferral,
    validation,
  } = useReferrals(effectiveReferralCode);
  const hasReferralAttribution = Boolean(
    effectiveReferralCode && isValidReferral,
  );

  const { height } = useWindowDimensions();
  const [verticalScroll, setVerticalScroll] = useState(0);
  const scrollViewRef = useRef<ScrollView | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(300);
  const [phoneSelection, setPhoneSelection] = useState({
    start: phoneNumber.length,
    end: phoneNumber.length,
  });

  const { challengeRedirect } = useRedirect();

  const handleBackButtonAction = () => {
    router.dismissTo({ pathname: '/login', params: urlSearchParams });
  };

  useBackHandler(handleBackButtonAction);

  const {
    headerText,
    subHeadText,
    headerHero,
    headerTextColor,
    headerSubHeadTextColor,
    headerBackgroundColor,
    headerImage,
    disclosureText,
    signupHeaderVariant,
  } = useSignupHeader(urlSearchParams, isValidReferral);

  const isSignupHeaderTreatment =
    signupHeaderVariant !== SIGNUP_HEADER_VARIANT.CONTROL;

  const utmContent = getParam(urlSearchParams, campaign.content);
  let iconColor = theme.color.text.primary;

  if (utmContent) {
    const shouldUseInverse = headerImage || headerHero === '';
    if (shouldUseInverse) {
      iconColor = theme.color.text.inverse;
    }
  }
  const { fetchWhereToGo } = useWhereToGo();
  const [hasPostSubmitChallenge, setHasPostSubmitChallenge] = useState(false);

  const {
    control,
    getValues,
    reset,
    setValue,
    setFocus,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      email: defaultEmail || '',
      phone: phoneNumber || '',
      password: '',
    },
    resolver: zodResolver(createAccountVerificationSchema),
  });

  useFocusEffect(
    useCallback(() => {
      void resetAuthState();
      reset({
        email: defaultEmail || '',
        phone: phoneNumber || '',
        password: '',
      });
      setPasswordValidations({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultEmail, phoneNumber, reset]),
  );

  const isPasswordValid =
    passwordValidations.minLength &&
    passwordValidations.hasUpperCase &&
    passwordValidations.hasLowerCase &&
    passwordValidations.hasNumber;
  const isCreateAccountFormValid = isValid && isPasswordValid;

  const isKeyboardVisible = useKeyboardVisibility();

  useAutoHideSnackbar(Boolean(snackbar.message), () =>
    setSnackbar({ message: '' }),
  );

  useEffect(() => {
    if (hasInvalidReferralCode) {
      setSnackbar({
        message: translatedText('referral.invalidCode'),
      });
    }
  }, [hasInvalidReferralCode, translatedText]);

  useEffect(() => {
    if (isLoggedIn && !hasPostSubmitChallenge) {
      void fetchWhereToGo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, hasPostSubmitChallenge]);

  const getCampaignObjectData = useCallback(
    (currentAttributionData: Record<string, unknown>) => {
      const campaignObject = getCampaignObjectAnalytics(
        hasReferralAttribution,
        urlSearchParams,
        effectiveReferralCode,
      );
      const campaignAttributionData = {
        ...campaignObject,
        ...currentAttributionData,
      };
      return campaignAttributionData;
    },
    [effectiveReferralCode, hasReferralAttribution, urlSearchParams],
  );

  const cookieObj = extractCookies();

  const handleAcquisitionCapturedAnalytics = useCallback(() => {
    const attributionData = getAttributionData();
    const key = `${screenKey}/${ACQUISITION_CAPTURED_ANALYTICS_ID}`;
    void analyticsEvents.logPressableEventFromConfig(key, {
      campaign: getCampaignObjectData(attributionData),
      email: getValues().email.split('@')[1] || '',
      referral_source: hasReferralAttribution ? 'referral_link' : 'blank',
      referral_validation_outcome: validation?.outcome,
      referral_validation_failure: validation?.failureReason,
      fbp: cookieObj.get('_fbp'),
    });
  }, [
    analyticsEvents,
    cookieObj,
    getCampaignObjectData,
    getValues,
    hasReferralAttribution,
    screenKey,
    validation,
  ]);

  const onSubmitEditingAction = async () => {
    if (
      isCreateAccountFormValid &&
      hasCaptchaToken({ xVaroCToken, isCloudflareTurnstileEnabledForNative })
    ) {
      return await onSubmit();
    }
  };

  const onSubmit = async () => {
    addTag(SentryTags.FRAUD, 'onSubmit CreateAccount');
    if (isCreateAccountFormValid) {
      addTag(SentryTags.FRAUD, 'isValid = true');

      const formData = getValues();
      setIsSignUpLoading(true);
      const anonymousId = await segmentClient.getAnonymousId();
      const phoneWithCountryCode = clearPhoneNumberFormatting(formData.phone);

      const aToken = await fetchIovationToken();
      const varoSessionId = await getVaroSessionId();

      const lambdaHeaders = await getLambdaHeaders(idToken, varoSessionId);

      const headers = {
        ...lambdaHeaders,
        ...{ 'x-varo-ctoken': xVaroCToken },
      };
      let response: CreateAccountResponse;

      try {
        response = await postCreateAccount(
          {
            emailAddress: formData.email,
            phoneNumber: phoneWithCountryCode,
            password: formData.password,
            anonymousId: anonymousId ?? '',
            aToken,
          },
          headers,
        );
      } catch (error) {
        applicationFlowErrorEncounteredAnalytics();
        logError(`/signup request error ${error}`);
        setSnackbar({
          message: 'Something went wrong. You can try again later.',
        });
        setIsSignUpLoading(false);
        return;
      }

      if (response.errorCode && response.message) {
        logErrorEvent(response.message, SourceName.SIGNUP_PAGE);
        setSnackbar({
          message: response.message,
        });
        setPasswordValidations({
          minLength: false,
          hasUpperCase: false,
          hasLowerCase: false,
          hasNumber: false,
        });
        reset({
          email: '',
          phone: '',
          password: '',
        });
        setIsSignUpLoading(false);
        logError(response.message);
        return;
      }
      try {
        if (response?.userId) {
          // Set minimal user identity during signup. Full identity will be synced after login.
          await syncMinimalUserIdentity(
            response.userId,
            formData.email,
            formData.phone,
          );
        }
      } catch {
        logError('Error in syncMinimalUserIdentity - createAccount');
      }

      try {
        const attributionData = getAttributionData();
        const campaignObject = getCampaignObject(
          hasReferralAttribution,
          urlSearchParams,
          response?.userId,
          effectiveReferralCode,
        );

        const mediaSource =
          typeof attributionData.media_source === 'string'
            ? attributionData.media_source
            : '';
        const campaignName =
          typeof attributionData.campaign === 'string'
            ? attributionData.campaign
            : '';
        const channel =
          typeof attributionData.af_channel === 'string'
            ? attributionData.af_channel
            : '';

        if (hasReferralAttribution) {
          // A referral must win over any AppsFlyer signal, organic or not -
          // otherwise the backend never sees the referral and no payout is created.
          campaignObject.acquisitionSource = 'Referral';
          campaignObject.acquisitionKeyword = 'referral';
        } else if (
          attributionData.af_status === AppsFlyerInstallStatus.NonOrganic &&
          mediaSource
        ) {
          campaignObject.acquisitionSource = mediaSource;
          campaignObject.acquisitionMedium = mediaSource;
          campaignObject.acquisitionCampaign = campaignName;
          campaignObject.acquisitionChannel = channel;
          campaignObject.acquisitionKeyword = AppsFlyerInstallStatus.NonOrganic;
        } else if (
          attributionData.af_status === AppsFlyerInstallStatus.Organic &&
          !campaignObject.acquisitionSource
        ) {
          campaignObject.acquisitionSource = AppsFlyerInstallStatus.Organic;
        }
        postSubmitAttributions(campaignObject).catch((error: unknown) => {
          void segmentClient.logEvent('Attribution Webhook Failed', {
            campaign: campaignObject,
            error,
          });
        });
      } catch {
        logErrorWithContext('Error in postSubmitAttributions - createAccount', {
          featureArea: FeatureArea.CUSTOMER_ATTRIBUTION,
          responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
          severity: ErrorSeverity.HIGH,
        });
      }
      setIsSignUpLoading(false);
      handleSubmitClickedAnalytics();
      handleAcquisitionCapturedAnalytics();

      await saveUserIDForReactNative(formData.email);
      await deleteRefreshTokenForReactNativeUserKey();
      await saveBiometricToggleSetting(false);

      // This code is hit 100% of the time for fresh users without a verified phone or email
      if (response.challenge) {
        setHasPostSubmitChallenge(true);

        return challengeRedirect(
          response.challenge,
          formData.email,
          response.phoneLineNumber,
          response.phoneLineNumber,
          response.session,
          'signup',
          OtpFlowKey.SIGNUP,
        );
      }

      /*
        This code is hit when a user has already successfully validated their phone and email and attempts to continue with account creation process
        via a signup "login" (signs up with the same email and phone number)
      */
      setIsSignUpLoading(false);
      await storeIdToken(response.idToken);
    }
  };

  // Function to validate the password
  const validatePassword = (value: string) => {
    setPasswordValidations(getPasswordValidationStatus(value));
  };

  const passwordMaskClicked = () => {
    handlePasswordMaskAnalytics();
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    setVerticalScroll(event.nativeEvent.contentOffset.y);
  };
  const scrollToFocusedTextField = (scrollTo: number) => {
    if (verticalScroll < scrollTo) {
      scrollViewRef?.current?.scrollTo({
        y: scrollTo,
        animated: true,
      });
    }
  };

  const getBottomPadding = (): number => {
    if (isKeyboardVisible) {
      return isAndroid
        ? keyboardHeight + getAndroidKeyboardHeightOffset(height)
        : bottom;
    }
    return theme.spacing.m;
  };

  const isCloudflareTurnstileEnabledForNative = isFeatureEnabled(
    FeatureFlagKey.ENABLE_CLOUDFLARE_TURNSTILE_NATIVE,
  );

  const isSignupDisabled =
    !hasCaptchaToken({ xVaroCToken, isCloudflareTurnstileEnabledForNative }) ||
    !isCreateAccountFormValid;

  return (
    <>
      <StatusBar style={headerHero || headerImage ? 'dark' : 'light'} />
      <Stack.Screen
        options={{
          title: '',
          headerShown: !isFetching,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              primaryOnPress={handleBackButtonAction}
              color={headerBackgroundColor || theme.color.expressive.sand}
              {...props}
              primaryIcon={<Icon iconKey="BACK" iconColor={iconColor} />}
            />
          ),
        }}
      />
      <View
        style={[styles.container, { paddingBottom: bottom }]}
        testID="create-account-screen"
      >
        <ScreenTransitionOverlay
          isLoading={isFetching}
          LoadingComponent={
            <View style={styles.loaderContainer}>
              <LoadingSpinner />
            </View>
          }
          ErrorComponent={null}
        />
        <KeyboardAvoidingView
          behavior={isIOS ? 'padding' : 'height'}
          style={styles.keyboardContainer}
          enabled={isIOS}
          keyboardVerticalOffset={getKeyboardVerticalOffset(
            height,
            isKeyboardVisible,
          )}
        >
          <ScrollView
            ref={scrollViewRef}
            keyboardShouldPersistTaps="handled"
            onScroll={handleScroll}
          >
            {isSignupHeaderTreatment ? (
              <SignupHeaderVariant
                headerText={headerText}
                subHeadText={subHeadText}
                headerTextColor={headerTextColor}
                headerBackgroundColor={headerBackgroundColor}
                headerSubHeadTextColor={headerSubHeadTextColor}
              />
            ) : (
              <SignupHeader
                headerText={headerText}
                subHeadText={subHeadText}
                headerHero={headerHero}
                headerTextColor={headerTextColor}
                headerBackgroundColor={headerBackgroundColor}
                headerSubHeadTextColor={headerSubHeadTextColor}
                headerImage={headerImage}
              />
            )}
            <View style={styles.subContainer}>
              {isValidReferral ? (
                <BodyBaseText style={styles.description} numberOfLines={0}>
                  {userName}
                  {translatedText('referral.title')}
                </BodyBaseText>
              ) : null}
              <BodyBaseText style={styles.description} numberOfLines={0}>
                {translatedText(
                  isSignupHeaderTreatment
                    ? 'header.variantDescription'
                    : 'description',
                )}
              </BodyBaseText>
              <Controller
                control={control}
                name="email"
                render={({
                  field: { onBlur, value },
                  fieldState: { error, isTouched },
                }) => {
                  const emailSuggestion = getEmailDomainSuggestion(value);
                  const handleEmailChange = (text: string) => {
                    setValue('email', text, {
                      shouldDirty: true,
                      shouldValidate: isTouched,
                    });
                  };

                  return (
                    <VXLTextInput
                      labelText={translatedText('emailField')}
                      errorText={error?.message}
                      onChangeText={handleEmailChange}
                      value={value}
                      autoComplete="email"
                      autoCorrect={false}
                      textContentType="emailAddress"
                      inputMode="email"
                      onBlur={onBlur}
                      autoCapitalize="none"
                      enterKeyHint="next"
                      autoFocus
                      onFocus={() => {
                        setKeyboardHeight(KEYBOARD_HEIGHT_FOR_TEXT_KEYPAD);
                        scrollToFocusedTextField(
                          KEYBOARD_SCROLL_OFFSET_PHONE_NUMBER,
                        );
                      }}
                      onSubmitEditing={() => setFocus('phone')}
                      analyticsId="emailFieldClicked"
                      testID="text-input-emailField"
                      suggestion={
                        emailSuggestion
                          ? {
                              suggestedValue: emailSuggestion,
                              accessibilityLabel: `Use suggested text: ${emailSuggestion}`,
                            }
                          : undefined
                      }
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="phone"
                render={({
                  field: { onBlur, ref, value },
                  fieldState: { isTouched },
                }) => {
                  const handlePhoneFocus = () => {
                    if (value.length === 0) {
                      setValue('phone', PHONE_PREFIX, {
                        shouldDirty: false,
                        shouldTouch: false,
                        shouldValidate: false,
                      });
                      if (isWeb) {
                        setPhoneSelection({
                          start: PHONE_PREFIX.length,
                          end: PHONE_PREFIX.length,
                        });
                      }
                    }
                    setKeyboardHeight(KEYBOARD_HEIGHT_FOR_PHONE_KEYPAD);
                    scrollToFocusedTextField(KEYBOARD_SCROLL_OFFSET_EMAIL);
                  };

                  const handlePhoneChange = (text: string) => {
                    const formattedPhoneNumber = formatPhoneInputText(text);
                    setValue('phone', formattedPhoneNumber, {
                      shouldDirty: true,
                      shouldValidate: isTouched,
                    });
                  };

                  return (
                    <VXLTextInput
                      ref={ref}
                      labelText={translatedText('uSMobileNumberField')}
                      onChangeText={handlePhoneChange}
                      value={value}
                      onBlur={onBlur}
                      inputMode="tel"
                      keyboardType="phone-pad"
                      maxLength={PHONE_MAX_LENGTH}
                      analyticsId="phoneFieldClicked"
                      onFocus={handlePhoneFocus}
                      enterKeyHint="next"
                      onSubmitEditing={() => setFocus('password')}
                      errorText={errors.phone?.message}
                      testID="text-input-uSMobileNumberField"
                      autoComplete="tel"
                      selection={isWeb ? phoneSelection : undefined}
                      onSelectionChange={
                        isWeb
                          ? (event) => {
                              setPhoneSelection(event.nativeEvent.selection);
                            }
                          : undefined
                      }
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="password"
                render={({ field: { onChange, onBlur, ref, value } }) => {
                  const handleChange = (e: string) => {
                    validatePassword(e);
                    onChange(e);
                    onBlur();
                  };
                  return (
                    <>
                      <VXLTextInput
                        ref={ref}
                        labelText={translatedText('passwordField')}
                        onChangeText={handleChange}
                        value={value}
                        onBlur={onBlur}
                        secureTextEntry={!isPasswordVisible}
                        autoCapitalize="none"
                        inputMode="text"
                        analyticsId="passwordFieldClicked"
                        onFocus={() => {
                          setKeyboardHeight(KEYBOARD_HEIGHT_FOR_TEXT_KEYPAD);
                          scrollToFocusedTextField(
                            KEYBOARD_HEIGHT_FOR_TEXT_KEYPAD,
                          );
                        }}
                        enterKeyHint="done"
                        onSubmitEditing={onSubmitEditingAction}
                        rightIcon={{
                          iconKey: !isPasswordVisible ? 'VIEW' : 'HIDE',
                          iconAccessibilityLabel: isPasswordVisible
                            ? 'eye-close'
                            : 'eye-open',
                          onPress: passwordMaskClicked,
                        }}
                        testID="text-input-passwordField"
                      />
                      <View style={styles.passwordValidationContainer}>
                        <View style={styles.validationItemContainer}>
                          <Icon
                            iconKey="STATUS_SUCCESS"
                            iconSize="SM"
                            iconColor={
                              passwordValidations.minLength
                                ? theme.color.interactive.primary
                                : theme.color.text.muted
                            }
                          />
                          <BodyXSText
                            testID={
                              passwordValidations.minLength
                                ? 'minLength-yes'
                                : 'minLength-no'
                            }
                            style={[
                              styles.validationText,
                              passwordValidations.minLength
                                ? styles.valid
                                : styles.invalid,
                            ]}
                            numberOfLines={0}
                          >
                            {passwordValidationMessages.minimumLength}
                          </BodyXSText>
                        </View>
                        <View style={styles.validationItemContainer}>
                          <Icon
                            iconKey="STATUS_SUCCESS"
                            iconSize="SM"
                            iconColor={
                              passwordValidations.hasLowerCase
                                ? theme.color.interactive.primary
                                : theme.color.text.muted
                            }
                          />
                          <BodyXSText
                            testID={
                              passwordValidations.hasLowerCase
                                ? 'hasLowerCase-yes'
                                : 'hasLowerCase-no'
                            }
                            style={[
                              styles.validationText,
                              passwordValidations.hasLowerCase
                                ? styles.valid
                                : styles.invalid,
                            ]}
                            numberOfLines={0}
                          >
                            {passwordValidationMessages.lowercase}
                          </BodyXSText>
                        </View>
                        <View style={styles.validationItemContainer}>
                          <Icon
                            iconKey="STATUS_SUCCESS"
                            iconSize="SM"
                            iconColor={
                              passwordValidations.hasUpperCase
                                ? theme.color.interactive.primary
                                : theme.color.text.muted
                            }
                          />
                          <BodyXSText
                            testID={
                              passwordValidations.hasUpperCase
                                ? 'hasUpperCase-yes'
                                : 'hasUpperCase-no'
                            }
                            style={[
                              styles.validationText,
                              passwordValidations.hasUpperCase
                                ? styles.valid
                                : styles.invalid,
                            ]}
                            numberOfLines={0}
                          >
                            {passwordValidationMessages.uppercase}
                          </BodyXSText>
                        </View>
                        <View style={styles.validationItemContainer}>
                          <Icon
                            iconKey="STATUS_SUCCESS"
                            iconSize="SM"
                            iconColor={
                              passwordValidations.hasNumber
                                ? theme.color.interactive.primary
                                : theme.color.text.muted
                            }
                          />
                          <BodyXSText
                            testID={
                              passwordValidations.hasNumber
                                ? 'hasNumber-yes'
                                : 'hasNumber-no'
                            }
                            style={[
                              styles.validationText,
                              passwordValidations.hasNumber
                                ? styles.valid
                                : styles.invalid,
                            ]}
                            numberOfLines={0}
                          >
                            {passwordValidationMessages.number}
                          </BodyXSText>
                        </View>
                      </View>
                    </>
                  );
                }}
              />
              {isValidReferral ? (
                <VXLTextInput
                  labelText={translatedText('referral.heading')}
                  value={effectiveReferralCode}
                  autoFocus={false}
                  readOnly={true}
                  editable={false}
                />
              ) : null}
              {disclosureText ? (
                <BodySmText style={styles.footer} numberOfLines={0}>
                  {translatedText(disclosureText)}
                </BodySmText>
              ) : null}
              {isSignupHeaderTreatment ? (
                <BodySmText style={styles.footer} numberOfLines={0}>
                  {translatedText('visaDisclosure')}
                </BodySmText>
              ) : null}
              <BodySmText style={styles.footer} numberOfLines={0}>
                {translatedText('footer')}
                <HyperlinkSmText
                  onPress={() => {
                    if (agreementUrl) {
                      handleAgreementClickedAnalytics();
                      router.push({
                        pathname: '/signup/agreement-viewer',
                        params: { pdfUrl: agreementUrl as string },
                      });
                    }
                  }}
                  numberOfLines={0}
                >
                  {translatedText('agreementText')}
                </HyperlinkSmText>
                <BodySmText style={styles.footerEnd} numberOfLines={0}>
                  {translatedText('footerEnd')}
                </BodySmText>
              </BodySmText>
              <VaroTurnstile
                setXVaroCToken={setXVaroCToken}
                xVaroCToken={xVaroCToken}
              />
            </View>
          </ScrollView>
          <View
            style={[
              styles.buttonContainer,
              {
                paddingBottom: getBottomPadding(),
              },
            ]}
          >
            <Button
              label={translatedText('buttons.joinToday')}
              onPress={onSubmit}
              isDisabled={isSignupDisabled}
              isLoading={isSignUpLoading}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
            />
          </View>
        </KeyboardAvoidingView>
        {!isKeyboardVisible ? (
          <View style={styles.termsAndCondition}>
            <BodyXSText style={styles.termsAndConditionColor} numberOfLines={0}>
              {translatedText('varobank')}
            </BodyXSText>
            <BodyXSText style={styles.termsAndConditionColor} numberOfLines={0}>
              {translatedText('allRightReserved', {
                year: new Date().getFullYear().toString(),
              })}
            </BodyXSText>
            {effectiveReferralCode ? (
              <HyperlinkXsText role="link" onPress={onPressSeeFullTerms}>
                {translatedText('seeFullTerms')}
              </HyperlinkXsText>
            ) : null}
          </View>
        ) : null}
        {snackbar.message ? (
          <Snackbar duration={SNACKBAR_DURATION} text={snackbar.message} />
        ) : null}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: `100%`,
  },
  description: {
    justifyContent: 'flex-start',
  },
  footer: {
    color: theme.color.text.secondary,
    justifyContent: 'flex-start',
    marginVertical: theme.spacing.s,
  },
  footerEnd: {
    color: theme.color.text.secondary,
  },
  invalid: {
    color: theme.color.text.muted,
  },
  keyboardContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
  },

  passwordValidationContainer: {
    marginBottom: theme.spacing.m,
    width: '100%',
  },
  subContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  termsAndCondition: {
    marginBottom: isAndroid ? theme.spacing.l : theme.spacing.xxl,
    marginHorizontal: theme.spacing.m,
  },
  termsAndConditionColor: {
    color: theme.color.text.tertiary,
  },
  valid: {
    color: theme.color.interactive.primary,
  },
  validationItemContainer: {
    flexDirection: 'row',
  },
  validationText: {
    marginBottom: theme.spacing.xs,
    paddingStart: theme.spacing.xs,
  },
});
